import React, { useState, useEffect, useMemo } from 'react';
import { FaFilter, FaSearch, FaSortAmountDown, FaSortAmountUp, FaChartLine, FaRupeeSign, FaPhone, FaEnvelope, FaLock, FaSignInAlt, FaPercentage, FaShieldAlt, FaExclamationTriangle, FaSync, FaInfoCircle, FaTags, FaIndustry, FaArrowUp, FaArrowDown, FaCaretDown, FaCaretUp, FaCheckCircle, FaTimesCircle, FaBalanceScale, FaCalculator, FaCalendarAlt, FaEye, FaCrown, FaSeedling, FaStar, FaFire, FaTag, FaBriefcase, FaMoneyBillWave, FaRocket, FaTrophy, FaBolt, FaChartBar, FaImage, FaChevronLeft, FaChevronRight, FaStepBackward, FaStepForward } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import axios from 'axios';

const ReturnScreener = () => {
  // ==================== START: LOGIN PROTECTION LOGIC ====================
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingLogin, setCheckingLogin] = useState(true);
  const [userData, setUserData] = useState(null);
  const [contentAccess, setContentAccess] = useState({ ReturnScreener: 0 });


    // Page metadata========================================================
  const [pageMetadata, setPageMetadata] = useState({
    page_names: 'IPO Research & Analysis',
    description: 'Comprehensive IPO data from SEBI Registered Research Analyst'
  });
  // Add this fetch function (around line 60-80)
  const fetchPageMetadata = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in'}/api/page-metadata/public/page9`);
      if (response.data) {
        setPageMetadata({
          page_names: response.data.page_name, // Using title from database
          description: response.data.description
        });
      }
    } catch (error) {
      console.error('Error fetching page metadata:', error);
      // Keep default values if fetch fails
    }
  };

  // Add this inside your existing useEffect or create a new one (around line 100-110)
  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.ReturnScreener === 1) {
      fetchPageMetadata(); // Add this line
    }
  }, [isLoggedIn, checkingLogin, contentAccess.ReturnScreener]);
  // Metadata End===========================================================
  
  const checkLoginStatus = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const userDataStr = localStorage.getItem('user');
      const loggedIn = !!(accessToken && userDataStr);
      setIsLoggedIn(loggedIn);
      
      if (loggedIn) {
        const user = JSON.parse(userDataStr);
        setUserData(user);
        await fetchContentAccess(user.id);
      } else {
        setUserData(null);
        setContentAccess({ ReturnScreener: 0 });
      }
      
      setCheckingLogin(false);
    } catch (error) {
      console.error('Error checking login status:', error);
      setCheckingLogin(false);
    }
  };
  
  const fetchContentAccess = async (userId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/content-access/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        setContentAccess(response.data.data);
      } else {
        setContentAccess({ ReturnScreener: 0 });
      }
    } catch (error) {
      console.error('Error fetching content access:', error);
      if (error.response?.status === 404) {
        setContentAccess({ ReturnScreener: 0 });
      }
    }
  };
  
  useEffect(() => {
    checkLoginStatus();
    
    const handleStorageChange = () => {
      checkLoginStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  // ==================== END: LOGIN PROTECTION LOGIC ====================

  // ==================== ORIGINAL COMPONENT STATE ====================
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' }); // No default sort
  const [selectedCategory, setSelectedCategory] = useState('all'); // Default 'all' shows all records without filter
  const [selectedStock, setSelectedStock] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [processedData, setProcessedData] = useState({ 
    stocks: [], 
    categories: [], 
    terms: [], 
    howToUse: [], 
    disclosures: [] 
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // API Configuration with Auto-refresh
  const API_URL = 'https://sheets.googleapis.com/v4/spreadsheets/1IcWlzXmkCMnBjH78Dth3jAjO2Uh68y25PEgDpB_FEhk/values/new%20return?key=AIzaSyBBDGaGd_H-xl8tjzlIZC-Yx2QLMM5FVUA';

  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.ReturnScreener === 1) {
      fetchScreenerData();
    } else {
      if (isLoggedIn && !checkingLogin) {
        setLoading(false);
      }
    }
  }, [isLoggedIn, checkingLogin, contentAccess.ReturnScreener]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.ReturnScreener === 1) {
      const intervalId = setInterval(() => {
        fetchScreenerData();
      }, 60000);
      
      return () => clearInterval(intervalId);
    }
  }, [isLoggedIn, checkingLogin, contentAccess.ReturnScreener]);

  // Process data when data changes
  useEffect(() => {
    if (data && data.values) {
      const result = processData(data.values);
      setProcessedData(result);
      setColumnHeaders(result.columnHeaders);
      
      // Extract all unique column names from stocks
      const columns = new Set();
      result.stocks.forEach(stock => {
        if (stock.dynamicData) {
          Object.keys(stock.dynamicData).forEach(key => {
            if (key && key.trim() !== '' && !key.includes('DYNAMICDATA') && !key.includes('COLUMNHEADERS')) {
              columns.add(key);
            }
          });
        }
      });
      setAllColumns(Array.from(columns));
    }
  }, [data]);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  const fetchScreenerData = async () => {
    try {
      setLoading(true);
      console.log('Fetching return screener data...');
      
      const response = await fetch(API_URL);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Return Screener API Response received:', result);
      
      if (!result.values) {
        throw new Error('No data found in the response');
      }
      
      setData(result);
      setLastUpdated(new Date());
      setLoading(false);
      
    } catch (err) {
      console.error('Error fetching screener data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Enhanced Dynamic data processing with NEW badge detection
  const processData = (values) => {
    if (!values) {
      console.log('No data available for processing');
      return { 
        stocks: [], 
        categories: [], 
        terms: [], 
        howToUse: [], 
        disclosures: [], 
        columnHeaders: [] 
      };
    }

    let stocks = [];
    let categories = new Set(['all']);
    let terms = [];
    let howToUse = [];
    let disclosures = [];
    let headers = [];
    let inStocksSection = false;
    let headersFound = false;
    let headersRowIndex = -1;

    // Process all rows
    for (let i = 0; i < values.length; i++) {
      const row = values[i];
      if (!row || row.length === 0) continue;

      const firstCell = row[0] ? row[0].toString().trim().toUpperCase() : '';
      
      // Check for stock table headers - Look for HEADER indicators
      if (!headersFound) {
        // Look for column headers pattern
        const potentialHeaders = [
          'COMPANY', 'TICKER', 'NAME', 'CMP', 'INTRINSIC VALUE', 
          'EXPECTED RETURN', 'CATEGORY', 'SECTOR', 'INDUSTRY', 'PE RATIO',
          'PB RATIO', 'ROE', 'ROCE', 'DEBT TO EQUITY', 'DIVIDEND YIELD',
          'MARKET CAP', 'PROMOTER HOLDING', 'FII HOLDING', 'DII HOLDING',
          'NEW'
        ];
        
        let headerMatches = 0;
        row.forEach(cell => {
          const cellUpper = cell ? cell.toString().trim().toUpperCase() : '';
          potentialHeaders.forEach(header => {
            if (cellUpper.includes(header)) {
              headerMatches++;
            }
          });
        });
        
        if (headerMatches >= 3) { // If at least 3 headers match
          inStocksSection = true;
          headersFound = true;
          headersRowIndex = i;
          headers = row.map(h => h ? h.toString().trim() : '');
          console.log('Found stock headers at row:', i, 'Headers:', headers);
          continue;
        }
      }

      // Process stock rows dynamically
      if (inStocksSection && headersFound && i > headersRowIndex) {
        // Skip empty rows
        if (row.length < 2) continue;
        
        // Stop if we find next section or empty section
        if (row.some(cell => cell && (
          cell.toString().includes('HIGH RETURN SCREENER') ||
          cell.toString().includes('TERMS USED') ||
          cell.toString().includes('WHAT IS HIGH RETURN') ||
          cell.toString().includes('DISCLAIMER') ||
          cell.toString().includes('HOW TO USE')
        ))) {
          inStocksSection = false;
          continue;
        }

        // Check if this looks like a data row (not a section header)
        const firstCellValue = row[0] ? row[0].toString().trim() : '';
        const hasImageUrl = firstCellValue.includes('http') && 
                          (firstCellValue.includes('.jpg') || 
                           firstCellValue.includes('.png') || 
                           firstCellValue.includes('.jpeg'));
        const hasTicker = row.some(cell => cell && cell.toString().includes(':'));
        const hasNumber = row.some(cell => {
          if (!cell) return false;
          const str = cell.toString();
          return !isNaN(parseFloat(str.replace(/,/g, ''))) && str.trim() !== '';
        });

        if (!hasImageUrl && !hasTicker && !hasNumber) {
          continue; // Skip non-data rows
        }

        // Create complete stock object with ALL columns
        const stockObj = {};
        let hasValidData = false;
        let sno = i - headersRowIndex; // Auto-generate SNO
        let isNew = false;
        
        // Map ALL columns dynamically
        headers.forEach((header, index) => {
          if (header && header.trim() !== '') {
            const rawValue = row[index] ? row[index].toString().trim() : '';
            
            // Check for NEW column
            if (header.toLowerCase().includes('new')) {
              isNew = rawValue.toLowerCase().includes('new');
            }
            
            // Store the value
            stockObj[header] = rawValue;
            
            // Check if this row has valid data
            if (rawValue && (
                header.toUpperCase().includes('NAME') || 
                header.toUpperCase().includes('COMPANY') ||
                header.toUpperCase().includes('TICKER'))) {
              hasValidData = true;
            }
          }
        });
        
        // If no ID was set, use row index
        stockObj.id = i;
        stockObj.sno = sno;
        
        if (hasValidData) {
          // Extract values with fallbacks
          const companyName = stockObj['NAME'] || stockObj['Company Name'] || stockObj['COMPANY'] || '';
          const ticker = stockObj['TICKER'] || stockObj['Ticker'] || stockObj['SYMBOL'] || '';
          const category = stockObj['CATEGORY'] || stockObj['Category'] || stockObj['RETURN CATEGORY'] || '';
          
          // Find CMP value (look in multiple columns)
          let cmp = 0;
          for (const [key, value] of Object.entries(stockObj)) {
            if ((key.toUpperCase().includes('CMP') || key.toUpperCase().includes('CURRENT PRICE')) && value) {
              const cleanValue = parseFloat(value.toString().replace(/[₹,]/g, ''));
              if (!isNaN(cleanValue)) {
                cmp = cleanValue;
                break;
              }
            }
          }
          
          // Find Intrinsic Value
          let intrinsicValue = 0;
          for (const [key, value] of Object.entries(stockObj)) {
            if ((key.toUpperCase().includes('INTRINSIC') || key.toUpperCase().includes('VALUE') || 
                 key.toUpperCase().includes('FAIR VALUE') || key.toUpperCase().includes('TARGET')) && 
                value && !key.toUpperCase().includes('RETURN')) {
              const cleanValue = parseFloat(value.toString().replace(/[₹,]/g, ''));
              if (!isNaN(cleanValue)) {
                intrinsicValue = cleanValue;
                break;
              }
            }
          }
          
          // Find Expected Return
          let expectedReturn = 0;
          for (const [key, value] of Object.entries(stockObj)) {
            if ((key.toUpperCase().includes('EXPECTED RETURN') || 
                 key.toUpperCase().includes('RETURN') ||
                 key.toUpperCase().includes('PROJECTED RETURN') ||
                 key.toUpperCase().includes('ESTIMATED RETURN')) && value) {
              const cleanValue = parseFloat(value.toString().replace(/[%,]/g, ''));
              if (!isNaN(cleanValue)) {
                expectedReturn = cleanValue;
                break;
              }
            }
          }
          
          // Determine category if not explicitly provided
          let finalCategory = category;
          if (!finalCategory) {
            // Auto-categorize based on expected return
            if (expectedReturn > 20) {
              finalCategory = 'VERY HIGH RETURN';
            } else if (expectedReturn > 15) {
              finalCategory = 'HIGH RETURN';
            } else if (expectedReturn > 10) {
              finalCategory = 'DECENT RETURN';
            } else if (expectedReturn > 5) {
              finalCategory = 'FAIR RETURN';
            } else {
              finalCategory = 'LOW RETURN';
            }
          }
          
          // Add category to categories set
          if (finalCategory) {
            categories.add(finalCategory);
          }
          
          // Calculate sector from company name or get from data
          let sector = stockObj['SECTOR'] || stockObj['Sector'] || stockObj['INDUSTRY'] || '';
          if (!sector) {
            sector = getSectorFromCompany(companyName);
          }
          
          // Calculate upside/downside
          const upside = intrinsicValue > 0 && cmp > 0 ? 
            ((intrinsicValue - cmp) / cmp) * 100 : 0;
          
          stocks.push({
            id: stockObj.id,
            sno: sno,
            companyName: companyName.trim(),
            ticker: ticker.trim(),
            cmp: cmp,
            intrinsicValue: intrinsicValue,
            expectedReturn: expectedReturn,
            category: finalCategory,
            sector: sector,
            upside: parseFloat(upside.toFixed(2)),
            isPositiveReturn: expectedReturn > 0,
            isNew: isNew, // Add isNew flag
            // Store ALL dynamic data
            dynamicData: stockObj,
            columnHeaders: headers,
            hasDynamicData: true,
            // Store image URL separately if it exists
            companyImage: stockObj['COMPANY'] && stockObj['COMPANY'].includes('http') ? 
                         stockObj['COMPANY'] : null
          });
        }
      }

      // Collect terms, how-to-use, and disclosures
      if (row.length > 0 && row[0]) {
        const text = row[0].toString();
        const textUpper = text.toUpperCase();
        
        if (textUpper.includes('TERMS USED')) {
          // Skip header
        } else if (textUpper.includes('HOW TO USE') || textUpper.includes('INSTRUCTIONS')) {
          // Skip header
        } else if (textUpper.includes('ALL THE BEST!') || textUpper.includes('ONE TOOL ALL SOLUTIONS')) {
          howToUse.push(text);
        } else if (textUpper.includes('?') || textUpper.includes('ANSWER:') || 
                  textUpper.includes('Q:') || textUpper.includes('A:')) {
          terms.push(text);
        } else if (textUpper.includes('SEBI') || textUpper.includes('DISCLOSURE') || 
                  textUpper.includes('DISCLAIMER') || textUpper.includes('RISK') ||
                  textUpper.includes('NOTE:')) {
          disclosures.push(text);
        }
      }
    }

    // Sort categories
    const sortedCategories = Array.from(categories)
      .filter(cat => cat !== 'all' && cat !== '')
      .sort((a, b) => a.localeCompare(b));

    console.log('Processed stocks:', stocks.length);
    console.log('Categories found:', sortedCategories);
    console.log('All columns found:', Array.from(new Set(stocks.flatMap(s => Object.keys(s.dynamicData || {})))));
    console.log('Sample stock:', stocks.length > 0 ? stocks[0] : 'No stocks');
    
    return { 
      stocks, 
      categories: sortedCategories,
      terms,
      howToUse,
      disclosures,
      columnHeaders: headers
    };
  };

  // Get ALL dynamic fields for display (excluding main fields)
  const getAllDisplayableFields = (stock) => {
    if (!stock.dynamicData) {
      return [];
    }
    
    const allFields = [];
    
    Object.entries(stock.dynamicData).forEach(([key, value]) => {
      const keyUpper = key.toUpperCase();
      
      // Skip empty values
      const isEmpty = value === undefined || value === null || 
                     value.toString().trim() === '';
      
      // Skip system fields and image URLs
      const isSystemField = keyUpper.includes('DYNAMICDATA') || 
                           keyUpper.includes('COLUMNHEADERS') || 
                           keyUpper.includes('HASDYNAMICDATA') ||
                           keyUpper === 'ID' ||
                           keyUpper === 'SNO';
      
      // Skip already displayed main fields
      const isMainField = keyUpper.includes('NAME') || 
                         keyUpper.includes('COMPANY') ||
                         keyUpper.includes('TICKER') ||
                         keyUpper.includes('CMP') ||
                         keyUpper.includes('INTRINSIC') ||
                         keyUpper.includes('VALUE') ||
                         keyUpper.includes('EXPECTED') ||
                         keyUpper.includes('RETURN') ||
                         keyUpper.includes('CATEGORY') ||
                         keyUpper === 'SECTOR' ||
                         keyUpper === 'UPSIDE' ||
                         keyUpper === 'ISPOSITIVERETURN' ||
                         keyUpper === 'NEW'; // Skip NEW column from additional data
      
      // Skip image URLs (handled separately)
      const isImageUrl = typeof value === 'string' && (
        value.includes('.jpg') || 
        value.includes('.png') || 
        value.includes('.jpeg') ||
        value.includes('http') && value.includes('logo')
      );
      
      if (!isEmpty && !isSystemField && !isMainField && !isImageUrl) {
        // Try to parse as number
        let numericValue = null;
        let isNumeric = false;
        
        if (typeof value === 'string') {
          // Clean the string for numeric parsing
          const cleanValue = value.replace(/[%,₹$]/g, '').replace(/,/g, '').trim();
          const parsedNum = parseFloat(cleanValue);
          if (!isNaN(parsedNum) && cleanValue !== '' && cleanValue !== '-') {
            numericValue = parsedNum;
            isNumeric = true;
          }
        } else if (typeof value === 'number') {
          numericValue = value;
          isNumeric = true;
        }
        
        const displayName = formatHeaderName(key);
        
        // Determine field type
        const isPercentage = keyUpper.includes('%') || 
                           keyUpper.includes('PERCENT') || 
                           keyUpper.includes('RETURN') ||
                           keyUpper.includes('YIELD') ||
                           keyUpper.includes('ROE') ||
                           keyUpper.includes('ROCE') ||
                           (typeof value === 'string' && value.includes('%'));
        
        const isCurrency = (keyUpper.includes('PRICE') || 
                          keyUpper.includes('VALUE') || 
                          keyUpper.includes('AMOUNT') || 
                          keyUpper.includes('CAP') ||
                          keyUpper.includes('₹') ||
                          (typeof value === 'string' && value.includes('₹'))) && 
                          !keyUpper.includes('RETURN');
        
        allFields.push({
          key,
          displayName,
          value: isNumeric ? numericValue : value,
          isNumeric: isNumeric,
          isPercentage: isPercentage,
          isCurrency: isCurrency,
          rawValue: value,
          isBoolean: typeof value === 'boolean' || 
                    (typeof value === 'string' && (value.toUpperCase() === 'YES' || value.toUpperCase() === 'NO'))
        });
      }
    });
    
    return allFields;
  };

  // Format header names for display
  const formatHeaderName = (header) => {
    return header
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/\./g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format value for display
  const formatValue = (field) => {
    // Handle numeric values
    if (field.isNumeric && typeof field.value === 'number') {
      if (field.isPercentage) {
        return `${field.value >= 0 ? '+' : ''}${field.value.toFixed(2)}%`;
      } else if (field.isCurrency) {
        return `₹${field.value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
      } else {
        if (field.value % 1 === 0) {
          return field.value.toLocaleString('en-IN');
        } else {
          return field.value.toFixed(2);
        }
      }
    }
    
    // Handle boolean values
    if (field.isBoolean) {
      if (typeof field.value === 'boolean') {
        return field.value ? 'Yes' : 'No';
      } else if (typeof field.value === 'string') {
        return field.value.toUpperCase() === 'YES' ? 'Yes' : 'No';
      }
    }
    
    // Handle string values
    if (typeof field.value === 'string') {
      // If it looks like a percentage string, keep it as is
      if (field.value.includes('%')) {
        return field.value;
      }
      // If it looks like a currency string, keep it as is
      if (field.value.includes('₹') || field.value.includes('$')) {
        return field.value;
      }
      // Return string as-is
      return field.value;
    }
    
    // For any other type, convert to string
    return String(field.value);
  };

  // Helper functions
  const getSectorFromCompany = (companyName) => {
    if (!companyName) return 'Diversified';
    const name = companyName.toLowerCase();
    
    if (name.includes('bank') || name.includes('finance') || name.includes('finserv')) {
      return 'Banking & Finance';
    } else if (name.includes('tech') || name.includes('technologies') || name.includes('software')) {
      return 'Technology';
    } else if (name.includes('construction') || name.includes('engineer') || name.includes('infra')) {
      return 'Infrastructure';
    } else if (name.includes('power') || name.includes('grid') || name.includes('energy')) {
      return 'Power & Utilities';
    } else if (name.includes('zinc') || name.includes('metal') || name.includes('steel') || name.includes('mining')) {
      return 'Metals & Mining';
    } else if (name.includes('cement')) {
      return 'Cement';
    } else if (name.includes('auto') || name.includes('motor') || name.includes('motocorp')) {
      return 'Automobile';
    } else if (name.includes('insurance') || name.includes('life')) {
      return 'Insurance';
    } else if (name.includes('pharma') || name.includes('laboratories') || name.includes('health')) {
      return 'Pharmaceuticals';
    } else if (name.includes('coal') || name.includes('mining')) {
      return 'Mining';
    } else if (name.includes('chemical') || name.includes('petro')) {
      return 'Chemicals';
    } else if (name.includes('oil') || name.includes('gas')) {
      return 'Oil & Gas';
    } else if (name.includes('retail') || name.includes('store')) {
      return 'Retail';
    } else if (name.includes('telecom') || name.includes('communication')) {
      return 'Telecom';
    } else {
      return 'Diversified';
    }
  };

  const getCategoryColor = (category) => {
    const catUpper = category ? category.toUpperCase() : '';
    if (catUpper.includes('BEST BUY') || catUpper.includes('VERY HIGH')) return 'bg-gradient-to-r from-green-500 to-green-600 text-white';
    if (catUpper.includes('HIGH')) return 'bg-gradient-to-r from-blue-500 to-blue-600 text-white';
    if (catUpper.includes('DECENT')) return 'bg-gradient-to-r from-purple-500 to-purple-600 text-white';
    if (catUpper.includes('FAIR') || catUpper.includes('AVERAGE')) return 'bg-gradient-to-r from-amber-500 to-amber-600 text-white';
    if (catUpper.includes('LOW') || catUpper.includes('UNDERVALUED')) return 'bg-gradient-to-r from-red-500 to-red-600 text-white';
    if (catUpper.includes('OVERVALUED')) return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white';
    return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
  };

  const getCategoryIcon = (category) => {
    const catUpper = category ? category.toUpperCase() : '';
    if (catUpper.includes('BEST BUY')) return <FaStar className="text-yellow-300" />;
    if (catUpper.includes('VERY HIGH')) return <FaRocket className="text-yellow-300" />;
    if (catUpper.includes('HIGH')) return <FaTrophy className="text-yellow-300" />;
    if (catUpper.includes('DECENT')) return <FaChartBar className="text-blue-300" />;
    if (catUpper.includes('FAIR') || catUpper.includes('AVERAGE')) return <FaBalanceScale className="text-green-300" />;
    if (catUpper.includes('LOW') || catUpper.includes('UNDERVALUED')) return <FaTimesCircle className="text-red-300" />;
    if (catUpper.includes('OVERVALUED')) return <FaExclamationTriangle className="text-gray-300" />;
    return <FaChartLine className="text-gray-300" />;
  };

  const { stocks, categories, terms, howToUse, disclosures } = processedData;

  // Filter and sort ALL stocks (no default category filter - 'all' shows everything)
  const filteredAndSortedStocks = useMemo(() => {
    let filtered = [...stocks];
    
    // Apply category filter ONLY if not 'all'
    if (selectedCategory !== 'all' && selectedCategory) {
      filtered = filtered.filter(stock => stock.category === selectedCategory);
    }
    
    // Apply search filter (works on ALL records)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(stock => 
        stock.companyName.toLowerCase().includes(term) ||
        stock.ticker.toLowerCase().includes(term) ||
        stock.sector.toLowerCase().includes(term) ||
        stock.category.toLowerCase().includes(term)
      );
    }
    
    // Apply sorting only if a sort key is selected
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        
        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    } else {
      // Preserve original order when no sort is applied
      filtered.sort((a, b) => (a.sno || 0) - (b.sno || 0));
    }
    
    return filtered;
  }, [stocks, selectedCategory, searchTerm, sortConfig]);

  // Calculate statistics for ALL filtered stocks
  const stats = useMemo(() => {
    if (filteredAndSortedStocks.length === 0) return null;
    
    const totalStocks = filteredAndSortedStocks.length;
    const veryHighReturnCount = filteredAndSortedStocks.filter(s => s.category === 'VERY HIGH RETURN').length;
    const highReturnCount = filteredAndSortedStocks.filter(s => s.category === 'HIGH RETURN').length;
    const avgExpectedReturn = filteredAndSortedStocks.reduce((sum, s) => sum + s.expectedReturn, 0) / totalStocks;
    const positiveReturnCount = filteredAndSortedStocks.filter(s => s.isPositiveReturn).length;
    const avgUpside = filteredAndSortedStocks.reduce((sum, s) => sum + s.upside, 0) / totalStocks;
    
    return {
      totalStocks,
      veryHighReturnCount,
      highReturnCount,
      avgExpectedReturn: parseFloat(avgExpectedReturn.toFixed(2)),
      positiveReturnCount,
      negativeReturnCount: totalStocks - positiveReturnCount,
      avgUpside: parseFloat(avgUpside.toFixed(2))
    };
  }, [filteredAndSortedStocks]);

  // Pagination calculations
  const totalPages = useMemo(() => {
    return Math.ceil(filteredAndSortedStocks.length / itemsPerPage);
  }, [filteredAndSortedStocks, itemsPerPage]);

  // Get current page stocks
  const currentPageStocks = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedStocks.slice(startIndex, endIndex);
  }, [filteredAndSortedStocks, currentPage, itemsPerPage]);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      
      if (endPage - startPage + 1 < maxPagesToShow) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }
    
    return pageNumbers;
  };

  // Handle sort click
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Clear all filters and reset
  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSortConfig({ key: null, direction: 'desc' });
  };

  // Pagination handlers
  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return '₹0';
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    }
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (!value || isNaN(value)) return '0.00%';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // ==================== LOGIN PROTECTION RENDER LOGIC ====================
  if (checkingLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
                <p className="text-gray-600 mb-1">You need to log in to access the Return Screener.</p>
                <p className="text-sm text-gray-500 mb-6">This content is exclusive to registered members.</p>
                <Link to="/login">
                  <button className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3">
                    <FaSignInAlt className="text-lg" />
                    Go to Login Page
                  </button>
                </Link>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Don't have an account?{' '}
                    <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                      Sign up here
                    </Link>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="opacity-40 pointer-events-none">
            <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">Return Stock Screener</h1>
                    <p className="text-blue-100">Identify high-potential stocks with maximum return projections</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="text-center py-20">
                <p className="text-gray-400">Content blurred for privacy. Please login to view.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  if (contentAccess.ReturnScreener !== 1) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-amber-100 to-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Content Access Restricted</h2>
                <p className="text-gray-600 mb-3">
                  Hello <span className="font-semibold text-blue-600">{userData?.username}</span>! 
                  You don't have access to the Return Screener yet.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  This premium content requires special access permissions.
                </p>
                <div className="space-y-3">
                  <a 
                    href="tel:+919319745997"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaPhone className="text-lg" />
                    Call Us: +91 9319745997
                  </a>
                  <a 
                    href="mailto:support@marketmaestroo.com"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaEnvelope className="text-lg" />
                    Email: support@marketmaestroo.com
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="opacity-40 pointer-events-none">
            <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">Return Stock Screener</h1>
                    <p className="text-blue-100">Identify high-potential stocks with maximum return projections</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="text-center py-20">
                <p className="text-gray-400">Content access restricted.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // ==================== MAIN RENDER LOGIC ====================
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Return Screener...</p>
          <p className="text-sm text-gray-500 mt-2">Auto-refresh every 60 seconds</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-100 max-w-md">
          <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Screener</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchScreenerData}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 mx-auto"
          >
            <FaSync />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">{pageMetadata.page_names}</h1>
                <p className="text-blue-100">{pageMetadata.description}</p>
                <p className="text-blue-200 text-sm mt-1">MAESTROO MYSTICAL TOOL (FIRST IN INDIA)</p>
              </div>
              <div className="mt-4 md:mt-0 flex flex-col md:flex-row items-center gap-4">
                <button
                  onClick={fetchScreenerData}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
                >
                  <FaSync className="text-sm" />
                  Refresh Now
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
         

          {/* Search and Filter Controls */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search stocks by name, ticker, sector, or category..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <FaTimesCircle />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Category Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="w-full md:w-auto px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-between gap-2"
                >
                  <div className="flex items-center gap-2">
                    <FaTags />
                    <span>
                      {selectedCategory === 'all' ? 'All Categories' : selectedCategory}
                    </span>
                  </div>
                  {showDropdown ? <FaCaretUp /> : <FaCaretDown />}
                </button>
                
                <AnimatePresence>
                  {showDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute z-10 mt-2 w-full md:w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-2 max-h-96 overflow-y-auto"
                    >
                      <button
                        onClick={() => {
                          setSelectedCategory('all');
                          setShowDropdown(false);
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between ${
                          selectedCategory === 'all' ? 'bg-blue-50 text-blue-700' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-gray-400"></span>
                          <span className="font-medium">All Categories</span>
                        </div>
                        
                      </button>
                      
                      {categories.map((category) => (
                        <button
                          key={category}
                          onClick={() => {
                            setSelectedCategory(category);
                            setShowDropdown(false);
                          }}
                          className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between ${
                            selectedCategory === category ? 'bg-blue-50 text-blue-700' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${
                              category.includes('BEST BUY') || category.includes('VERY HIGH') ? 'bg-green-500' :
                              category.includes('HIGH') ? 'bg-blue-500' :
                              category.includes('DECENT') ? 'bg-purple-500' :
                              category.includes('FAIR') || category.includes('AVERAGE') ? 'bg-amber-500' :
                              category.includes('LOW') || category.includes('UNDERVALUED') ? 'bg-red-500' :
                              category.includes('OVERVALUED') ? 'bg-gray-500' :
                              'bg-gray-400'
                            }`}></span>
                            <span className="font-medium">{category}</span>
                          </div>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              <button
                onClick={clearAllFilters}
                className="px-4 py-3 rounded-lg transition-colors flex items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
              >
                <FaTimesCircle />
                Clear Filters
              </button>
            </div>

            {/* Sort Controls */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
              <span className="text-gray-600 font-medium">Sort by:</span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleSort('expectedReturn')}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    sortConfig.key === 'expectedReturn' 
                      ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  Expected Return
                  {sortConfig.key === 'expectedReturn' && (
                    sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                  )}
                </button>
                <button
                  onClick={() => handleSort('upside')}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    sortConfig.key === 'upside' 
                      ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  Upside Potential
                  {sortConfig.key === 'upside' && (
                    sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                  )}
                </button>
                <button
                  onClick={() => handleSort('cmp')}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    sortConfig.key === 'cmp' 
                      ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  Current Price
                  {sortConfig.key === 'cmp' && (
                    sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                  )}
                </button>
                <button
                  onClick={() => handleSort('category')}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    sortConfig.key === 'category' 
                      ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  Category
                  {sortConfig.key === 'category' && (
                    sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                  )}
                </button>
              </div>
              {sortConfig.key && (
                <button
                  onClick={() => setSortConfig({ key: null, direction: 'desc' })}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  <FaTimesCircle className="text-xs" />
                  Clear Sort
                </button>
              )}
              <div className="ml-auto text-sm text-gray-500">
                {filteredAndSortedStocks.length} stocks found • Page {currentPage} of {totalPages}
              </div>
            </div>
          </div>

          {/* Stock Cards - Card Design */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            

            {filteredAndSortedStocks.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-200">
                <FaFilter className="text-gray-400 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Stocks Found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm 
                    ? `No stocks found matching "${searchTerm}" in ${selectedCategory === 'all' ? 'all categories' : selectedCategory}`
                    : `No stocks found in ${selectedCategory === 'all' ? 'any category' : selectedCategory}`
                  }
                </p>
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                  }}
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {currentPageStocks.map((stock, index) => {
                    const dynamicFields = getAllDisplayableFields(stock);
                    const globalIndex = (currentPage - 1) * itemsPerPage + index + 1;
                    
                    return (
                      <motion.div
                        key={stock.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        whileHover={{ y: -4 }}
                        className="group relative"
                      >
                        {/* New Badge - Display as diagonal ribbon in top right corner */}
                        {stock.isNew && (
                          <div className="absolute top-0 right-0 z-20 overflow-hidden w-24 h-24">
                            <div 
                              className="absolute top-10 transform -translate-x-1/2 -translate-y-1/2 rotate-45 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold py-1 px-6 shadow-lg"
                              style={{ right: "-57px", top: "18px" }}
                            >
                              NEW
                            </div>
                          </div>
                        )}

                        {/* Stock Card */}
                        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-200 transition-all duration-300 overflow-hidden hover:border-blue-300 hover:shadow-xl h-full">
                          

                          {/* Category Banner */}
                          <div className={`${getCategoryColor(stock.category)} px-4 py-2 flex items-center justify-between`}>
                            <div className="flex items-center gap-2">
                              {getCategoryIcon(stock.category)}
                              <span className="text-xs font-bold">{stock.category}</span>
                            </div>
                            <div className={`text-xs px-2 py-1 rounded-full ${stock.isPositiveReturn ? 'bg-green-500/30 text-green-100' : 'bg-red-500/30 text-red-100'}`}>
                              {formatPercentage(stock.expectedReturn)}
                            </div>
                          </div>

                          <div className="p-5">
                            {/* Stock Header with Image */}
                            <div className="flex items-start gap-3 mb-4">
                              {/* Company Image */}
                              {stock.companyImage && (
                                <div className="flex-shrink-0">
                                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                                    <img 
                                      src={stock.companyImage} 
                                      alt={stock.companyName}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gray-100"><FaImage className="text-gray-300" /></div>';
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                              
                              {/* Company Info */}
                              <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-gray-900 text-lg line-clamp-2 group-hover:text-blue-600 transition-colors">
                                  {stock.companyName}
                                </h3>
                               
                              </div>
                            </div>

                            {/* Ticker */}
                            {stock.ticker && (
                              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                                <div className="text-xs text-blue-600 mb-1">Ticker</div>
                                <div className="font-bold text-blue-700 text-sm truncate">{stock.ticker}</div>
                              </div>
                            )}

                            {/* Core Metrics Section */}
                            <div className="mb-4">
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div className="bg-gray-50 p-3 rounded-lg">
                                  <div className="text-xs text-gray-500 mb-1">CMP</div>
                                  <div className="font-bold text-gray-900 text-lg">{formatCurrency(stock.cmp)}</div>
                                </div>
                                <div className="bg-green-50 p-3 rounded-lg">
                                  <div className="text-xs text-green-600 mb-1">Intrinsic Value</div>
                                  <div className="font-bold text-green-700 text-lg">{formatCurrency(stock.intrinsicValue)}</div>
                                </div>
                              </div>

                              {/* Return Metrics */}
                              <div className={`p-3 rounded-lg ${stock.isPositiveReturn ? 'bg-green-50' : 'bg-red-50'}`}>
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-gray-700">5-Year Expected Return</span>
                                  <div className={`text-lg font-bold ${stock.isPositiveReturn ? 'text-green-600' : 'text-red-600'} flex items-center gap-1`}>
                                    {stock.isPositiveReturn ? <FaArrowUp className="text-sm" /> : <FaArrowDown className="text-sm" />}
                                    {formatPercentage(stock.expectedReturn)}
                                  </div>
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  Upside: {formatPercentage(stock.upside)}
                                </div>
                              </div>
                            </div>

                            {/* ALL Dynamic Fields Section - Shows everything else */}
                            {dynamicFields.length > 0 && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-sm font-semibold text-gray-700">Additional Data</h4>
                                  <span className="text-xs text-gray-500">
                                    {dynamicFields.length} fields
                                  </span>
                                </div>
                                
                                <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                                  {dynamicFields.map((field, idx) => (
                                    <div 
                                      key={idx} 
                                      className={`p-2 rounded-lg ${
                                        field.isPercentage ? 'bg-blue-50' :
                                        field.isCurrency ? 'bg-green-50' :
                                        field.isBoolean ? 'bg-purple-50' :
                                        'bg-gray-50'
                                      }`}
                                    >
                                      <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                          <div className="text-xs font-medium text-gray-600 mb-0.5">
                                            {field.displayName}
                                          </div>
                                          <div className={`text-sm font-bold ${
                                            field.isPercentage 
                                              ? (typeof field.value === 'number' ? (field.value >= 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-900')
                                              : field.isCurrency ? 'text-blue-700' : 
                                              field.isBoolean ? 'text-purple-700' : 'text-gray-900'
                                          }`}>
                                            {formatValue(field)}
                                          </div>
                                        </div>
                                        {field.isPercentage && typeof field.value === 'number' && (
                                          <div className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                                            field.value >= 0 
                                              ? 'bg-green-100 text-green-800' 
                                              : 'bg-red-100 text-red-800'
                                          }`}>
                                            {field.value >= 0 ? '+' : ''}{field.value.toFixed(1)}%
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4"
                  >
                    <div className="text-sm text-gray-600">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedStocks.length)} of {filteredAndSortedStocks.length} stocks
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* First Page */}
                      <button
                        onClick={goToFirstPage}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-lg ${
                          currentPage === 1 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <FaStepBackward />
                      </button>
                      
                      {/* Previous Page */}
                      <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-lg ${
                          currentPage === 1 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <FaChevronLeft />
                      </button>
                      
                      {/* Page Numbers */}
                      <div className="flex items-center gap-1">
                        {getPageNumbers().map((pageNumber) => (
                          <button
                            key={pageNumber}
                            onClick={() => goToPage(pageNumber)}
                            className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                              currentPage === pageNumber
                                ? 'bg-blue-600 text-white font-semibold'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        ))}
                        
                        {totalPages > 5 && currentPage < totalPages - 2 && (
                          <>
                            <span className="px-2 text-gray-400">...</span>
                            <button
                              onClick={() => goToPage(totalPages)}
                              className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                                currentPage === totalPages
                                  ? 'bg-blue-600 text-white font-semibold'
                                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {totalPages}
                            </button>
                          </>
                        )}
                      </div>
                      
                      {/* Next Page */}
                      <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-lg ${
                          currentPage === totalPages 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <FaChevronRight />
                      </button>
                      
                      {/* Last Page */}
                      <button
                        onClick={goToLastPage}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-lg ${
                          currentPage === totalPages 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <FaStepForward />
                      </button>
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </motion.div>

          {/* Terms & How to Use Section */}
          {(terms.length > 0 || howToUse.length > 0) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-12"
            >
              <div className="bg-gradient-to-r from-gray-50 to-white rounded-2xl border border-gray-200 p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <FaInfoCircle className="text-blue-500" />
                  Tool Information
                </h3>
                
                <div className="space-y-8">
                  {terms.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <FaBalanceScale className="text-green-500" />
                        Terms Used in Screener
                      </h4>
                      <div className="space-y-4">
                        {terms.map((term, index) => (
                          <div key={index} className="bg-white/50 p-5 rounded-xl border border-gray-100">
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                              {term.replace(/\r\n/g, '\n')}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {howToUse.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <FaCalculator className="text-purple-500" />
                        How to Use the Tool
                      </h4>
                      <div className="space-y-4">
                        {howToUse.map((instruction, index) => (
                          <div key={index} className="bg-white/50 p-5 rounded-xl border border-gray-100">
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                              {instruction.replace(/\r\n/g, '\n')}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Disclosures */}
          {disclosures.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-8"
            >
              <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl border border-red-100 p-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FaShieldAlt className="text-red-500" />
                  Important Disclosures
                </h4>
                <div className="space-y-3">
                  {disclosures.map((disclosure, index) => (
                    <div key={index} className="text-sm text-gray-600 leading-relaxed">
                      {disclosure.replace(/\r\n/g, '\n')}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ReturnScreener;